import * as React from 'react';
import type { ButtonDropdownProps } from '@amzn/awsui-components-console';
import { ButtonDropdown, TopNavigation } from '@amzn/awsui-components-console';
import { styled } from '@linaria/react';
import { ANTENNA_SITE_TIME_ZONES, formatTimeInZone } from '../utils/dateTimeUtilities';
import { useAppSettings, useAppSettingsModalState } from '../context/AppSettings';
import { NAV_I18NSTRINGS } from '../constants/i18nStrings';
import { accountsHelper } from '../utils/accountsUtils';
import { useCazJustification } from '../context/Permissions';
import { LOGO_BASE_64 } from './icons/logo';
import { SettingsModal } from './SettingsModal';
import { ContingentAuthModal } from './ContingentAuthDialog';
import { useModalState } from '../hooks/useModalState';

/**
 * Quick action links shown in the header "Quick Actions" dropdown.
 * Each item links out to an external tool, so `external` is set to open in a new tab
 * (preserving the `target: '_blank'` behavior these had as standalone header buttons).
 */
const QUICK_ACTION_ITEMS = [
  {
    id: 'feature-request',
    text: 'Feature Request',
    href: 'https://issues.amazon.com/issues/create?template=0fcdeef1-2dde-4426-98ae-8811e0aed369',
    external: true,
  },
  {
    id: 'bug-report',
    text: 'Bug Report',
    href: 'https://t.corp.amazon.com/create/templates/eef1e103-653e-47c1-9e66-3e840263ff02',
    external: true,
  },
  {
    id: 'launch-agent-spaces',
    text: 'Launch Agent Spaces',
    href: 'https://agentspaces.amazon.dev/app/?mode=CUSTOM&blueprint=AwsGsaasGSOCFrontendWebsite&aimPackageName=AIPowerUserCapabilities&customAgentName=gpu-coder',
    external: true,
  },
];

/** Common header for all pages */
export default function CommonHeader() {
  const { stage, region } = useAppSettings();
  const [cazJustification] = useCazJustification();
  const [settingsModalVisible, settingsModalActions] = useAppSettingsModalState();
  const [cazModalVisible, cazModalActions] = useModalState();

  const regionSelectionItems = React.useMemo(
    () =>
      accountsHelper.getAccounts('opsconsole', stage).map(account => {
        const endpoint = account.services.find(service => service.service_key === 'gsoc')?.endpoint;
        if (endpoint === undefined) {
          throw new Error('Unable to get gsoc account');
        }
        return {
          id: account.region_abbr,
          text: account.region,
          href: `https://${endpoint}/#`,
          variant: 'link',
        };
      }),
    [stage],
  );

  return (
    <>
      <TopNavigation
        identity={{
          href: '/#',
          title: 'AWS Ground Station - Operations Console (GSOC)',
          logo: {
            src: LOGO_BASE_64,
            alt: 'GSOC',
          },
        }}
        utilities={[
          {
            type: 'menu-dropdown',
            text: 'Quick Actions',
            description: 'GSOC quick actions',
            items: QUICK_ACTION_ITEMS,
          },
          {
            type: 'menu-dropdown',
            text: window.location.hostname === 'dev-user.amazon.com' ? `dev-user(${region})` : region,
            description: 'Select Ops Console Region',
            items: regionSelectionItems,
          },
          // This is soooo hacky, but it works. Cloudscape doesn't support custom elements in the header (dumb)
          { type: 'button', text: (<UtcTimeStamp />) as unknown as string },
          // Live local time at each antenna ground-station site.
          { type: 'button', text: (<SiteTimeZonesDropdown />) as unknown as string },
          // This is soooo hacky, but it works. Cloudscape doesn't support custom elements in the header (dumb)
          {
            type: 'button',
            text: (cazJustification !== null ? (
              <CazEnabledSpan>CAZ Enabled</CazEnabledSpan>
            ) : (
              'CAZ'
            )) as unknown as string,
            onClick: cazModalActions.open,
          },
          {
            type: 'button',
            iconName: 'settings',
            onClick: settingsModalActions.open,
          },
        ]}
        i18nStrings={NAV_I18NSTRINGS}
      />
      <ContingentAuthModal visible={cazModalVisible} closeModal={cazModalActions.close} />
      <SettingsModal visible={settingsModalVisible} closeModal={settingsModalActions.close} />
    </>
  );
}

const TimestampSpan = styled.span`
  cursor: text;
  font-weight: normal;
`;

const CazEnabledSpan = styled.span`
  font-weight: bold;
  color: #ff0000;
`;

function UtcTimeStamp() {
  const [utcTime, setUtcTime] = React.useState(new Date());
  React.useEffect(() => {
    const interval = setInterval(() => {
      setUtcTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  return <TimestampSpan>{`${utcTime.toISOString().slice(0, -5)}Z`}</TimestampSpan>;
}

const CLEAR_SITE_SELECTION_ID = 'clear-site-selection';

// Exported for direct testing: inside the full `CommonHeader`, `TopNavigation`
// hides this via ResizeObserver overflow measurements that jsdom lacks.
export function SiteTimeZonesDropdown() {
  const [now, setNow] = React.useState(new Date());
  const [selectedSiteId, setSelectedSiteId] = React.useState<string | null>(null);
  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const items = React.useMemo(() => {
    const siteItems = ANTENNA_SITE_TIME_ZONES.map(site => ({
      id: site.id,
      text: `${site.label} — ${formatTimeInZone(now, site.timeZone)}`,
    }));
    return selectedSiteId === null
      ? siteItems
      : [{ id: CLEAR_SITE_SELECTION_ID, text: 'Clear selection' }, ...siteItems];
  }, [now, selectedSiteId]);

  const onItemClick = React.useCallback((event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
    setSelectedSiteId(event.detail.id === CLEAR_SITE_SELECTION_ID ? null : event.detail.id);
  }, []);

  const selectedSite = ANTENNA_SITE_TIME_ZONES.find(site => site.id === selectedSiteId);
  const label = selectedSite ? `${selectedSite.label} — ${formatTimeInZone(now, selectedSite.timeZone)}` : 'Site Times';

  return (
    <ButtonDropdown
      items={items}
      onItemClick={onItemClick}
      ariaLabel='Antenna site local times'
      expandableGroups={false}
    >
      {label}
    </ButtonDropdown>
  );
}
